import { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

const categories = [
  {
    id: 'publications',
    label: 'Publications and Authors',
    icon: `${process.env.PUBLIC_URL}/categoryIcons/publications.svg`,
    secondaryIcon: `${process.env.PUBLIC_URL}/categoryIcons/people.svg`,
    secondaryLabel: 'Authors',
  },
  {
    id: 'patents',
    label: 'Patents and Inventors',
    icon: `${process.env.PUBLIC_URL}/categoryIcons/patents.svg`,
    secondaryIcon: `${process.env.PUBLIC_URL}/categoryIcons/people.svg`,
    secondaryLabel: 'Inventors',
  },
  {
    id: 'funding',
    label: 'Funding',
    icon: `${process.env.PUBLIC_URL}/categoryIcons/funding.svg`,
  },
  {
    id: 'jobs',
    label: 'Jobs',
    icon: `${process.env.PUBLIC_URL}/categoryIcons/jobs.svg`,
  },
  {
    id: 'organizations',
    label: 'Organizations',
    icon: `${process.env.PUBLIC_URL}/categoryIcons/organizations.svg`,
  },
  {
    id: 'events',
    label: 'Events',
    icon: `${process.env.PUBLIC_URL}/categoryIcons/events.svg`,
  },
];

const initialSelections = {
  publications: false,
  patents: false,
  funding: false,
  jobs: false,
  organizations: false,
  events: false,
};

export default function VerticalResourceSearchModal({ open, onClose, onRunSearch, busy = false }) {
  const [selections, setSelections] = useState(initialSelections);

  useEffect(() => {
    if (open) {
      setSelections(initialSelections);
    }
  }, [open]);

  const selectedCount = Object.values(selections).filter(Boolean).length;

  const handleToggle = (id) => {
    setSelections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleRunSearch = () => {
    const selectedCategories = Object.entries(selections)
      .filter(([, checked]) => checked)
      .map(([category]) => category);

    onRunSearch(selectedCategories);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '10px',
          paddingTop: '6px',
          maxWidth: '480px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 800,
          lineHeight: 1.1,
          paddingBottom: '6px',
          textAlign: 'left',
        }}
      >
        Select Categories to Search
      </DialogTitle>

      <DialogContent
        sx={{
          paddingTop: '4px !important',
          paddingBottom: '12px',
          textAlign: 'left',
        }}
      >
        <p
          style={{
            margin: '0 0 28px',
            fontSize: '13px',
            lineHeight: 1.4,
            textAlign: 'left',
          }}
        >
          Running a Discovery Refresh counts as one full refresh, regardless of how many categories
          are selected. The People category is currently populated from authors and inventors found
          in Publications and Patents, so it is included when those categories are selected. We plan
          to support independent People searches in a future update.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {categories.map((category) => (
            <label
              key={category.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '18px',
                cursor: 'pointer',
                width: 'fit-content',
              }}
            >
              <Checkbox
                checked={!!selections[category.id]}
                onChange={() => handleToggle(category.id)}
                sx={{
                  padding: 0,
                  color: '#1F1F1F',
                  '&.Mui-checked': {
                    color: '#1F1F1F',
                  },
                }}
              />

              <img
                src={category.icon}
                alt={`${category.label} icon`}
                style={{ width: '28px', height: '28px', objectFit: 'contain' }}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#111111',
                  fontSize: '16px',
                  lineHeight: 1.1,
                  textAlign: 'left',
                }}
              >
                {category.secondaryIcon ? (
                  <>
                    <span>{category.label.split(' and ')[0]} and</span>
                    <img
                      src={category.secondaryIcon}
                      alt={`${category.secondaryLabel} icon`}
                      style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                    />
                    <span>{category.secondaryLabel}</span>
                  </>
                ) : (
                  <span>{category.label}</span>
                )}
              </div>
            </label>
          ))}
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          padding: '20px 24px 24px',
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          disabled={busy}
          sx={{
            backgroundColor: 'gray',
            '&:hover': {
              backgroundColor: '#666666',
              boxShadow: 'none',
            },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleRunSearch}
          disabled={selectedCount === 0 || busy}
        >
          Run Search
        </Button>
      </DialogActions>
    </Dialog>
  );
}
